import { Type } from '@angular/core';

export interface GameAnswerEntry {
  loader: () => Promise<Type<any>>;
  /** Extra static inputs beyond the common ones (idSection, idTheme, idLevel, gameName, gameResult) */
  inputs?: Record<string, any>;
}

// Default fallback: AnswerGlobalComponent with no extra inputs
export const DEFAULT_ANSWER_LOADER = () =>
  import('../../../components/games/answers/answer-global/answer-global.component').then(m => m.AnswerGlobalComponent);

export const GAME_ANSWER_REGISTRY: Record<string, GameAnswerEntry> = {

  // ── AnswerGlobalComponent (explicit extra inputs) ─────────────────────────
  word_mistake: {
    loader: DEFAULT_ANSWER_LOADER,
    inputs: { sound: false },
  },
  word_mistake_easy: {
    loader: DEFAULT_ANSWER_LOADER,
    inputs: { sound: false },
  },
  sentence_mistake: {
    loader: DEFAULT_ANSWER_LOADER,
    inputs: { sound: false },
  },
  sentence_mistake_easy: {
    loader: DEFAULT_ANSWER_LOADER,
    inputs: { sound: false },
  },
  find_word_from_image: {
    loader: DEFAULT_ANSWER_LOADER,
    inputs: { sound: false, image: true },
  },
  find_image_from_sound: {
    loader: DEFAULT_ANSWER_LOADER,
    inputs: { sound: true, image: true },
  },
  find_image_from_word: {
    loader: DEFAULT_ANSWER_LOADER,
    inputs: { sound: false, image: true },
  },
  find_sound_from_image: {
    loader: DEFAULT_ANSWER_LOADER,
    inputs: { sound: true, image: true },
  },
  find_from_image: {
    loader: DEFAULT_ANSWER_LOADER,
    inputs: { sound: true, image: true },
  },
  chrono_word: {
    loader: DEFAULT_ANSWER_LOADER,
    inputs: { sound: false, image: false },
  },
  find_translation_from_word: {
    loader: DEFAULT_ANSWER_LOADER,
    inputs: { sound: false, image: false },
  },
  find_translation_from_sentence: {
    loader: DEFAULT_ANSWER_LOADER,
    inputs: { sound: false, image: false },
  },
  ja_find_word_from_kanji: {
    loader: DEFAULT_ANSWER_LOADER,
    inputs: { sound: false, image: false },
  },
  connect_words: {
    loader: DEFAULT_ANSWER_LOADER,
    inputs: { sound: false, image: false },
  },
  chrono_sentence: {
    loader: DEFAULT_ANSWER_LOADER,
    inputs: { sound: false, image: false },
  },
  toeic_first_listening: {
    loader: DEFAULT_ANSWER_LOADER,
    inputs: { sound: false, image: true },
  },

  // ── AnswerLotoComponent ───────────────────────────────────────────────────
  loto: {
    loader: () => import('../../../components/games/answers/answer-loto/answer-loto.component').then(m => m.AnswerLotoComponent),
    inputs: { sound: true },
  },

  // ── AnswerFindGraphemeFromWordComponent ───────────────────────────────────
  find_grapheme_from_word: {
    loader: () => import('../../../components/games/answers/answer-find-grapheme-from-word/answer-find-grapheme-from-word.component').then(m => m.AnswerFindGraphemeFromWordComponent),
    inputs: { sound: false, image: false },
  },

  // ── AnswerSentenceOrderComponent ──────────────────────────────────────────
  sentence_order: {
    loader: () => import('../../../components/games/answers/answer-sentence-order/answer-sentence-order.component').then(m => m.AnswerSentenceOrderComponent),
  },

  // ── AnswerFindItemComponent ───────────────────────────────────────────────
  find_item: {
    loader: () => import('../../../components/games/answers/answer-find-item/answer-find-item.component').then(m => m.AnswerFindItemComponent),
  },
  find_item_from_image: {
    loader: () => import('../../../components/games/answers/answer-find-item/answer-find-item.component').then(m => m.AnswerFindItemComponent),
  },

  // ── AnswerVerbConjugationComponent ────────────────────────────────────────
  verb_conjugation: {
    loader: () => import('../../../components/games/answers/answer-verb-conjugation/answer-verb-conjugation.component').then(m => m.AnswerVerbConjugationComponent),
  },

  // ── AnswerFindGenderComponent ─────────────────────────────────────────────
  find_gender: {
    loader: () => import('../../../components/games/answers/answer-find-gender/answer-find-gender.component').then(m => m.AnswerFindGenderComponent),
  },

  // ── AnswerDictationComponent ──────────────────────────────────────────────
  word_dictation_to_source: {
    loader: () => import('../../../components/games/answers/answer-dictation/answer-dictation.component').then(m => m.AnswerDictationComponent),
  },
  word_dictation: {
    loader: () => import('../../../components/games/answers/answer-dictation/answer-dictation.component').then(m => m.AnswerDictationComponent),
  },
  sentence_dictation: {
    loader: () => import('../../../components/games/answers/answer-dictation/answer-dictation.component').then(m => m.AnswerDictationComponent),
  },

  // ── AnswerGraphemeOrderComponent ──────────────────────────────────────────
  grapheme_order_audio: {
    loader: () => import('../../../components/games/answers/answer-grapheme-order/answer-grapheme-order.component').then(m => m.AnswerGraphemeOrderComponent),
  },
  grapheme_order_text: {
    loader: () => import('../../../components/games/answers/answer-grapheme-order/answer-grapheme-order.component').then(m => m.AnswerGraphemeOrderComponent),
  },

  // ── AnswerFindPhonemeComponent ────────────────────────────────────────────
  find_phoneme_from_grapheme: {
    loader: () => import('../../../components/games/answers/answer-find-phoneme/answer-find-phoneme.component').then(m => m.AnswerFindPhonemeComponent),
  },
  find_phoneme_from_grapheme_syllable: {
    loader: () => import('../../../components/games/answers/answer-find-phoneme/answer-find-phoneme.component').then(m => m.AnswerFindPhonemeComponent),
  },

  // ── AnswerFindTimeComponent ───────────────────────────────────────────────
  find_time_from_word: {
    loader: () => import('../../../components/games/answers/answer-find-time/answer-find-time.component').then(m => m.AnswerFindTimeComponent),
    inputs: { sound: false, image: false },
  },
  find_time_from_sentence: {
    loader: () => import('../../../components/games/answers/answer-find-time/answer-find-time.component').then(m => m.AnswerFindTimeComponent),
    inputs: { sound: false, image: false },
  },
  find_pronoun_from_verb: {
    loader: () => import('../../../components/games/answers/answer-find-time/answer-find-time.component').then(m => m.AnswerFindTimeComponent),
    inputs: { sound: false, image: false },
  },
  ja_find_time_or_form: {
    loader: () => import('../../../components/games/answers/answer-find-time/answer-find-time.component').then(m => m.AnswerFindTimeComponent),
    inputs: { sound: false, image: false },
  },

  // ── AnswerFindGraphemeComponent ───────────────────────────────────────────
  find_grapheme_from_phoneme: {
    loader: () => import('../../../components/games/answers/answer-find-grapheme/answer-find-grapheme.component').then(m => m.AnswerFindGraphemeComponent),
  },
  ja_find_grapheme_hiragana_simple: {
    loader: () => import('../../../components/games/answers/answer-find-grapheme/answer-find-grapheme.component').then(m => m.AnswerFindGraphemeComponent),
  },
  ja_find_grapheme_katakana_simple: {
    loader: () => import('../../../components/games/answers/answer-find-grapheme/answer-find-grapheme.component').then(m => m.AnswerFindGraphemeComponent),
  },
  ja_find_grapheme_katakana_hiragana_simple: {
    loader: () => import('../../../components/games/answers/answer-find-grapheme/answer-find-grapheme.component').then(m => m.AnswerFindGraphemeComponent),
  },
  ja_find_grapheme_hiragana: {
    loader: () => import('../../../components/games/answers/answer-find-grapheme/answer-find-grapheme.component').then(m => m.AnswerFindGraphemeComponent),
  },
  ja_find_grapheme_katakana: {
    loader: () => import('../../../components/games/answers/answer-find-grapheme/answer-find-grapheme.component').then(m => m.AnswerFindGraphemeComponent),
  },
  ja_find_grapheme_katakana_hiragana: {
    loader: () => import('../../../components/games/answers/answer-find-grapheme/answer-find-grapheme.component').then(m => m.AnswerFindGraphemeComponent),
  },
  find_grapheme_syllable_from_phoneme: {
    loader: () => import('../../../components/games/answers/answer-find-grapheme/answer-find-grapheme.component').then(m => m.AnswerFindGraphemeComponent),
  },

  // ── AnswerFindGrammaticalCaseComponent ────────────────────────────────────
  find_grammatical_case: {
    loader: () => import('../../../components/games/answers/answer-find-grammatical-case/answer-find-grammatical-case.component').then(m => m.AnswerFindGrammaticalCaseComponent),
  },

  // ── AnswerWritingDeclensionComponent ──────────────────────────────────────
  writing_declension: {
    loader: () => import('../../../components/games/answers/answer-writing-declension/answer-writing-declension.component').then(m => m.AnswerWritingDeclensionComponent),
  },

  // ── AnswerGapTextComponent ────────────────────────────────────────────────
  gap_text: {
    loader: () => import('../../../components/games/answers/answer-gap-text/answer-gap-text.component').then(m => m.AnswerGapTextComponent),
  },

  // ── AnswerSkillGapTextComponent ───────────────────────────────────────────
  skill_gap_text: {
    loader: () => import('../../../components/games/answers/answer-skill-gap-text/answer-skill-gap-text.component').then(m => m.AnswerSkillGapTextComponent),
  },

  // ── AnswerOnlyScoreComponent ──────────────────────────────────────────────
  dialogue: {
    loader: () => import('../../../components/games/answers/answer-only-score/answer-only-score.component').then(m => m.AnswerOnlyScoreComponent),
  },
  zh_find_accent_from_sound: {
    loader: () => import('../../../components/games/answers/answer-only-score/answer-only-score.component').then(m => m.AnswerOnlyScoreComponent),
  },
  zh_draw_from_word: {
    loader: () => import('../../../components/games/answers/answer-only-score/answer-only-score.component').then(m => m.AnswerOnlyScoreComponent),
  },
  zh_draw_from_sound: {
    loader: () => import('../../../components/games/answers/answer-only-score/answer-only-score.component').then(m => m.AnswerOnlyScoreComponent),
  },

  // ── AnswerAudioOrderComponent ─────────────────────────────────────────────
  audio_order: {
    loader: () => import('../../../components/games/answers/answer-audio-order/answer-audio-order.component').then(m => m.AnswerAudioOrderComponent),
  },

  // ── AnswerSudokuComponent ─────────────────────────────────────────────────
  sudoku: {
    loader: () => import('../../../components/games/answers/answer-sudoku/answer-sudoku.component').then(m => m.AnswerSudokuComponent),
  },

  // ── AnswerCalculsComponent ────────────────────────────────────────────────
  calculs: {
    loader: () => import('../../../components/games/answers/answer-calculs/answer-calculs.component').then(m => m.AnswerCalculsComponent),
  },

  // ── AnswerFindIrregularVerbComponent ──────────────────────────────────────
  en_find_irregular_verb: {
    loader: () => import('../../../components/games/answers/answer-find-irregular-verb/answer-find-irregular-verb.component').then(m => m.AnswerFindIrregularVerbComponent),
  },

  // ── AnswerFromToComponent ─────────────────────────────────────────────────
  ja_find_kanji_from_kana: {
    loader: () => import('../../../components/games/answers/answer-from-to/answer-from-to.component').then(m => m.AnswerFromToComponent),
    inputs: { sound: false },
  },
  ja_find_kana_from_kanji: {
    loader: () => import('../../../components/games/answers/answer-from-to/answer-from-to.component').then(m => m.AnswerFromToComponent),
    inputs: { sound: false },
  },
  ja_find_kanji_from_sound: {
    loader: () => import('../../../components/games/answers/answer-from-to/answer-from-to.component').then(m => m.AnswerFromToComponent),
    inputs: { sound: false },
  },

  // ── AnswerFindConjugationComponent ────────────────────────────────────────
  ja_find_conjugation: {
    loader: () => import('../../../components/games/answers/answer-find-conjugation/answer-find-conjugation.component').then(m => m.AnswerFindConjugationComponent),
    inputs: { sound: false },
  },

  // ── AnswerFindKeyComponent ────────────────────────────────────────────────
  zh_find_key_from_word: {
    loader: () => import('../../../components/games/answers/answer-find-key/answer-find-key.component').then(m => m.AnswerFindKeyComponent),
    inputs: { sound: false },
  },

  // ── AnswerWriteFromToComponent ────────────────────────────────────────────
  zh_write_pinyin_from_sound: {
    loader: () => import('../../../components/games/answers/answer-write-from-to/answer-write-from-to.component').then(m => m.AnswerWriteFromToComponent),
  },
  zh_write_pinyin_from_word: {
    loader: () => import('../../../components/games/answers/answer-write-from-to/answer-write-from-to.component').then(m => m.AnswerWriteFromToComponent),
  },

  // ── AnswerFindPositionFromGraphemeComponent ───────────────────────────────
  ar_find_position_from_grapheme: {
    loader: () => import('../../../components/games/answers/answer-find-position-from-grapheme/answer-find-position-from-grapheme.component').then(m => m.AnswerFindPositionFromGraphemeComponent),
  },

  // ── AnswerFindGraphemeFromPositionComponent ───────────────────────────────
  ar_find_grapheme_from_position: {
    loader: () => import('../../../components/games/answers/answer-find-grapheme-from-position/answer-find-grapheme-from-position.component').then(m => m.AnswerFindGraphemeFromPositionComponent),
  },

  // ── AnswerSecondReadingComponent ──────────────────────────────────────────
  toeic_second_reading: {
    loader: () => import('../../../components/games/answers/answer-second-reading/answer-second-reading.component').then(m => m.AnswerSecondReadingComponent),
  },
  toeic_third_reading: {
    loader: () => import('../../../components/games/answers/answer-second-reading/answer-second-reading.component').then(m => m.AnswerSecondReadingComponent),
  },
};
